import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Paper,
  FormControlLabel,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  PushPin as PushPinIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';
import { BaseButton } from '../../../base';
import BaseModal from '../../../base/BaseModal';
import { ConfigScheme, SchemaPermissionConfig } from '../../../../../services/ConfigManagementService';
import configService from '../../../../../services/ConfigManagementService';

const FONT = '"Manrope", sans-serif';
const ACCENT = '#005e5d';
const ACCENT_LIGHT = '#005e5d14';
const TEXT_PRIMARY = '#333333';
const TEXT_SECONDARY = '#666666';
const TEXT_TERTIARY = '#999999';
const BORDER = '#E6E7EB';
const SURFACE = '#F2F3F5';

export interface SchemaTabProps {
  schemes: ConfigScheme[];
  loading: boolean;
  onCreateSchema: () => void;
  onEditSchema: (key: string) => void;
  onDeleteSchema: (key: string) => void;
  onRefresh: () => void;
}

// ─── Export Options Modal ────────────────────────────────────────────────────
interface ExportModalProps {
  open: boolean;
  schemes: ConfigScheme[];
  onClose: () => void;
}

const ExportModal: React.FC<ExportModalProps> = ({ open, schemes, onClose }) => {
  const [includeData, setIncludeData] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleKey = (key: string) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      await configService.exportSchemas(selectedKeys, includeData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Reset on open
  React.useEffect(() => {
    if (open) { setIncludeData(false); setSelectedKeys([]); setError(null); }
  }, [open]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Export Schemas"
      maxWidth="sm"
      onSubmit={handleExport}
      isLoading={exporting}
      submitButtonText="Download ZIP"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', color: TEXT_SECONDARY }}>
          Select schemas to export. Leave all unchecked to export everything.
        </Typography>

        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '8px', maxHeight: 240, overflowY: 'auto' }}>
          <List dense disablePadding>
            {schemes.map((s, idx) => (
              <ListItem
                key={s.key}
                onClick={() => toggleKey(s.key)}
                selected={selectedKeys.includes(s.key)}
                sx={{
                  cursor: 'pointer',
                  borderBottom: idx < schemes.length - 1 ? `1px solid ${BORDER}` : 'none',
                  '&.Mui-selected': { backgroundColor: ACCENT_LIGHT },
                }}
              >
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', fontWeight: 600, color: TEXT_PRIMARY }}>
                      {s.key}
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: TEXT_TERTIARY }}>
                      {s.type} · v{s.version}{s.description ? ` · ${s.description}` : ''}
                    </Typography>
                  }
                />
                {selectedKeys.includes(s.key) && (
                  <Chip label="Selected" size="small" sx={{ backgroundColor: ACCENT, color: '#fff', fontFamily: FONT, fontSize: '0.7rem' }} />
                )}
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider />

        <FormControlLabel
          control={
            <Switch
              checked={includeData}
              onChange={(e) => setIncludeData(e.target.checked)}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT } }}
            />
          }
          label={
            <Box>
              <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', color: TEXT_PRIMARY, fontWeight: 600 }}>Include Data</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: TEXT_TERTIARY }}>Export config values and collection items alongside the schema definitions</Typography>
            </Box>
          }
        />

        {error && (
          <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: '#D32F2F' }}>{error}</Typography>
        )}
      </Box>
    </BaseModal>
  );
};

// ─── Import Modal ─────────────────────────────────────────────────────────────
interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

const ImportModal: React.FC<ImportModalProps> = ({ open, onClose, onSuccess }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (open) { setFile(null); setOverwrite(false); setResults(null); setError(null); }
  }, [open]);

  const handleImport = async () => {
    if (!file) { setError('Please select a ZIP file'); return; }
    try {
      setImporting(true);
      setError(null);
      const result = await configService.importSchemas(file, overwrite);
      setResults(result.results);
      const { created, updated, skipped } = result.summary;
      onSuccess(`Import complete: ${created} created, ${updated} updated, ${skipped} skipped`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const statusColor = (status: string) => {
    if (status === 'created') return '#2E7D32';
    if (status === 'updated') return '#1565C0';
    return '#9E9E9E';
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Import Schemas"
      maxWidth="sm"
      onSubmit={results ? onClose : handleImport}
      isLoading={importing}
      submitButtonText={results ? 'Done' : 'Import'}
      cancelButtonText="Cancel"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!results ? (
          <>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', color: TEXT_SECONDARY }}>
              Upload a ZIP file previously exported from this system.
            </Typography>

            <Box
              onClick={() => fileRef.current?.click()}
              sx={{
                border: `2px dashed ${file ? ACCENT : BORDER}`,
                borderRadius: '10px',
                py: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: ACCENT },
              }}
            >
              <ImportIcon sx={{ fontSize: 32, color: file ? ACCENT : TEXT_TERTIARY, mb: 0.5 }} />
              <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', color: file ? ACCENT : TEXT_TERTIARY, fontWeight: file ? 600 : 400 }}>
                {file ? file.name : 'Click to select ZIP file'}
              </Typography>
              <input ref={fileRef} type="file" accept=".zip" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={overwrite}
                  onChange={(e) => setOverwrite(e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#D32F2F' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D32F2F' } }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', color: TEXT_PRIMARY, fontWeight: 600 }}>Overwrite existing</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: TEXT_TERTIARY }}>Replace schemas and data that already exist</Typography>
                </Box>
              }
            />

            {error && <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: '#D32F2F' }}>{error}</Typography>}
          </>
        ) : (
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.875rem', color: TEXT_PRIMARY, mb: 1 }}>Import Results</Typography>
            <List dense disablePadding sx={{ border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden' }}>
              {results.map((r: any, idx: number) => (
                <ListItem key={r.key} sx={{ borderBottom: idx < results.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                  <ListItemText
                    primary={<Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', fontWeight: 600, color: TEXT_PRIMARY }}>{r.key}</Typography>}
                    secondary={r.error && <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: '#D32F2F' }}>{r.error}</Typography>}
                  />
                  <Chip
                    label={r.status}
                    size="small"
                    sx={{ fontFamily: FONT, fontSize: '0.7rem', fontWeight: 700, backgroundColor: `${statusColor(r.status)}22`, color: statusColor(r.status) }}
                  />
                  {r.dataImported && <Chip label="data" size="small" sx={{ ml: 0.5, fontFamily: FONT, fontSize: '0.7rem', backgroundColor: ACCENT_LIGHT, color: ACCENT }} />}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </BaseModal>
  );
};

const headerCellSx = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: '0.75rem',
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  color: TEXT_SECONDARY,
  backgroundColor: SURFACE,
  borderBottom: `1px solid ${BORDER}`,
  py: 1.5,
  px: 2,
};

const bodyCellSx = {
  fontFamily: FONT,
  fontSize: '0.875rem',
  color: TEXT_PRIMARY,
  borderBottom: `1px solid ${BORDER}`,
  py: 1.5,
  px: 2,
};

const PERM_ACTION_COLOR: Record<string, { bg: string; color: string }> = {
  create: { bg: '#E8F5E9', color: '#2E7D32' },
  read:   { bg: '#E3F2FD', color: '#1565C0' },
  update: { bg: '#FFF3E0', color: '#E65100' },
  delete: { bg: '#FFEBEE', color: '#C62828' },
};

const PermissionCell: React.FC<{ schemaKey: string }> = ({ schemaKey }) => {
  const [perm, setPerm] = useState<SchemaPermissionConfig | null>(null);

  useEffect(() => {
    configService.getPermission(schemaKey).then(setPerm).catch(() => {});
  }, [schemaKey]);

  if (!perm) return <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: '#CCCCCC' }}>—</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {(['create', 'read', 'update', 'delete'] as const).map((action) => {
        const roles: string[] = (perm as any)[action] ?? [];
        const c = PERM_ACTION_COLOR[action];
        return (
          <Box key={action} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ px: 0.75, py: 0.2, borderRadius: '4px', backgroundColor: c.bg, minWidth: 44 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: '0.65rem', fontWeight: 700, color: c.color, textTransform: 'uppercase' }}>
                {action}
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: TEXT_TERTIARY }}>
              {roles.length === 0 ? 'nobody' : roles.join(', ')}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export const SchemaTab: React.FC<SchemaTabProps> = ({
  schemes,
  loading,
  onCreateSchema,
  onEditSchema,
  onDeleteSchema,
  onRefresh,
}) => {
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importSuccessMsg, setImportSuccessMsg] = useState<string | null>(null);

  const handleImportSuccess = (msg: string) => {
    setImportSuccessMsg(msg);
    onRefresh();
    setTimeout(() => setImportSuccessMsg(null), 5000);
  };

  return (
    <Box>
      <ExportModal open={exportOpen} schemes={schemes} onClose={() => setExportOpen(false)} />
      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onSuccess={handleImportSuccess} />

      {importSuccessMsg && (
        <Box sx={{ mb: 2, px: 2, py: 1.25, backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: '8px' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>{importSuccessMsg}</Typography>
        </Box>
      )}
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: '1.125rem',
              color: TEXT_PRIMARY,
              lineHeight: 1.3,
            }}
          >
            Schema List
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: '0.8125rem',
              color: TEXT_TERTIARY,
              mt: 0.25,
            }}
          >
            {schemes.length} schema{schemes.length !== 1 ? 's' : ''} defined
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={onRefresh}
              disabled={loading}
              size="small"
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: '8px',
                color: TEXT_SECONDARY,
                '&:hover': { backgroundColor: SURFACE, borderColor: '#D0D2D7' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <BaseButton
            variant="outlined"
            colorVariant="secondary"
            startIcon={<ImportIcon />}
            onClick={() => setImportOpen(true)}
            size="small"
          >
            Import
          </BaseButton>
          <BaseButton
            variant="outlined"
            colorVariant="secondary"
            startIcon={<ExportIcon />}
            onClick={() => setExportOpen(true)}
            disabled={schemes.length === 0}
            size="small"
          >
            Export
          </BaseButton>
          <BaseButton
            variant="contained"
            colorVariant="primary"
            startIcon={<AddIcon />}
            onClick={onCreateSchema}
            size="small"
          >
            Create Schema
          </BaseButton>
        </Box>
      </Box>

      {/* Content */}
      {loading && !schemes.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress size={32} sx={{ color: ACCENT }} />
        </Box>
      ) : schemes.length === 0 ? (
        <Box
          sx={{
            border: `2px dashed ${BORDER}`,
            borderRadius: '12px',
            py: 6,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontFamily: FONT, fontSize: '0.9375rem', color: TEXT_TERTIARY, mb: 0.5 }}>
            No schemas found
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: TEXT_TERTIARY }}>
            Create a schema to get started.
          </Typography>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '10px',
            border: `1px solid ${BORDER}`,
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Key</TableCell>
                  <TableCell sx={headerCellSx}>Description</TableCell>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx}>Permissions</TableCell>
                  <TableCell sx={headerCellSx}>Version</TableCell>
                  <TableCell sx={headerCellSx}>Pinned</TableCell>
                  <TableCell sx={headerCellSx}>Created</TableCell>
                  <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schemes.map((scheme) => (
                  <TableRow
                    key={scheme.key}
                    sx={{
                      '&:hover': { backgroundColor: SURFACE },
                      '&:last-child td': { borderBottom: 'none' },
                    }}
                  >
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: TEXT_PRIMARY }}>
                      {scheme.label || scheme.key}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: TEXT_TERTIARY }}>
                      {scheme.description || '—'}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: scheme.type === 'collection' ? ACCENT : TEXT_TERTIARY,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: scheme.type === 'collection' ? ACCENT : TEXT_SECONDARY,
                          }}
                        >
                          {scheme.type === 'collection' ? 'Collection' : 'Config'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, verticalAlign: 'top', py: 1 }}>
                      <PermissionCell schemaKey={scheme.key} />
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: TEXT_SECONDARY }}>
                      v{scheme.version}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {scheme.pinToSidebar ? (
                        <Tooltip title="Pinned to sidebar Manager section">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backgroundColor: ACCENT_LIGHT,
                              borderRadius: '6px',
                              px: 1,
                              py: 0.4,
                            }}
                          >
                            <PushPinIcon sx={{ fontSize: 13, color: ACCENT }} />
                            <Typography
                              sx={{
                                fontFamily: FONT,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: ACCENT,
                              }}
                            >
                              Pinned
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', color: '#CCCCCC' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: TEXT_TERTIARY, fontSize: '0.8125rem' }}>
                      {new Date(scheme.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                      <Tooltip title="Edit schema">
                        <IconButton
                          size="small"
                          onClick={() => onEditSchema(scheme.key)}
                          sx={{
                            color: ACCENT,
                            '&:hover': { backgroundColor: ACCENT_LIGHT },
                            borderRadius: '6px',
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete schema">
                        <IconButton
                          size="small"
                          onClick={() => onDeleteSchema(scheme.key)}
                          sx={{
                            color: '#D32F2F',
                            '&:hover': { backgroundColor: 'rgba(211,47,47,0.08)' },
                            borderRadius: '6px',
                            ml: 0.5,
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default SchemaTab;
